<template>
  <div v-if="show" class="modal" @click="close">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>{{ title }}</h3>
        <button class="close-btn" @click="close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="map-container">
          <div id="google-map" ref="googleMap"></div>
          <div class="map-coordinates">
            <span>Coordinates: ({{ formattedCoordinates }})</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ViewLocationModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    latitude: {
      type: [Number, String],
      required: true
    },
    longitude: {
      type: [Number, String],
      required: true
    },
    title: {
      type: String,
      default: "Location"
    },
    markerTitle: {
      type: String,
      default: "Location"
    },
    zoom: {
      type: Number,
      default: 15
    }
  },
  emits: ["close"],
  data() {
    return {
      map: null,
      marker: null
    };
  },
  computed: {
    formattedCoordinates() {
      return `${this.latitude}, ${this.longitude}`;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initMap();
        });
      } else {
        this.cleanupMap();
      }
    },
    latitude() {
      if (this.show) {
        this.updateMap();
      }
    },
    longitude() {
      if (this.show) {
        this.updateMap();
      }
    }
  },
  methods: {
    close() {
      this.$emit("close");
    },
    initMap() {
      // Check if Google Maps API is already loaded
      if (!window.google) {
        this.loadGoogleMapsAPI();
        return;
      }

      this.renderMap();
    },
    loadGoogleMapsAPI() {
      // Add Google Maps API script dynamically
      const script = document.createElement('script');
      const googleKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;

      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&callback=initMap`;
      script.async = true;
      script.defer = true;

      // Define the callback function
      window.initLocationMapCallback = () => {
        this.renderMap();
      };

      document.head.appendChild(script);
    },
    renderMap() {
      if (!this.$refs.googleMap) return;

      const lat = parseFloat(this.latitude);
      const lng = parseFloat(this.longitude);

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng)) {
        console.error("Invalid coordinates provided to ViewLocationModal");
        return;
      }

      this.map = new google.maps.Map(this.$refs.googleMap, {
        center: { lat: 7.8731, lng: 80.7718 }, // Sri Lanka center
        zoom: 7
      });

      // Add marker
      this.marker = new google.maps.Marker({
        position: { lat, lng },
        map: this.map,
        draggable: false, // Enable route dragging
        title: this.markerTitle,
      });
    },
    updateMap() {
      if (!this.map || !this.marker) return;

      const lat = parseFloat(this.latitude);
      const lng = parseFloat(this.longitude);

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng)) {
        console.error("Invalid coordinates provided to ViewLocationModal");
        return;
      }

      const newPosition = { lat, lng };

      // Update map center and marker position
      this.map.setCenter(newPosition);
      this.marker.setPosition(newPosition);
    },
    cleanupMap() {
      if (this.map) {
        this.map = null;
      }
      if (this.marker) {
        this.marker = null;
      }
    }
  },
  beforeUnmount() {
    this.cleanupMap();
  }
};
</script>

<style scoped lang="scss">
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.7);
  z-index: 2000;
}

.modal-content {
  background-color: #ffffff;
  border-radius: 12px;
  width: 95%;       /* increased */
  height: 90%;      /* increased */
  max-width: 900px; /* increased */
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: white;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: #374151;
  }
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 1.25rem;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #374151;
  }
}

.modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.map-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

#google-map {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 350px;
}

.map-coordinates {
  padding: 0.5rem 1.5rem;
  background-color: #f8fafc;
  border-top: 1px solid #e5e7eb;
  font-size: 0.875rem;
  color: #64748b;
  text-align: center;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    height: 85%;
  }

  .modal-header {
    padding: 0.75rem 1rem;

    h3 {
      font-size: 1.1rem;
    }
  }
}
</style>